/**
 * Tree Diagram Viewer
 *
 * Visualizes structured data (JSON/YAML/XML) as a node-link diagram.
 */

import React from 'react';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { IVisualizerTool, VisualizerRenderContext } from '@/interfaces/ITool';
import { parseJsonLenient } from '@/lib/jsonParser';

import { DEFAULT_SEARCH_MODE } from './JsonDiagram.shared';

const LazyJsonDiagramView = React.lazy(() =>
  import('./JsonDiagramView').then((m) => ({ default: m.JsonDiagramView }))
);
import { parse as parseYaml } from 'yaml';

type InputFormat = 'auto' | 'json' | 'yaml' | 'xml';

function looksLikeXml(text: string): boolean {
  const t = text.trim();
  return t.startsWith('<') && t.endsWith('>');
}

function xmlToObject(node: Node): unknown {
  // Element
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as Element;
    const out: Record<string, unknown> = {};

    // Attributes
    if (el.attributes && el.attributes.length > 0) {
      const attrs: Record<string, string> = {};
      for (const attr of Array.from(el.attributes)) {
        attrs[`@${attr.name}`] = attr.value;
      }
      out._attributes = attrs;
    }

    // Children
    const childElements = Array.from(el.childNodes).filter((c) => c.nodeType === Node.ELEMENT_NODE);
    const textNodes = Array.from(el.childNodes)
      .filter((c) => c.nodeType === Node.TEXT_NODE)
      .map((c) => (c.textContent ?? '').trim())
      .filter(Boolean);

    // If no element-children, return text (or empty string) with optional attrs
    if (childElements.length === 0) {
      const text = textNodes.join(' ');
      if (Object.keys(out).length === 0) return text;
      if (text) out._text = text;
      return out;
    }

    const grouped: Record<string, unknown[]> = {};
    for (const child of childElements) {
      const childEl = child as Element;
      const key = childEl.tagName;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(xmlToObject(childEl) as unknown);
    }

    for (const [k, items] of Object.entries(grouped)) {
      out[k] = items.length === 1 ? items[0] : items;
    }

    // Preserve any significant direct text alongside children
    if (textNodes.length > 0) {
      out._text = textNodes.join(' ');
    }

    return out;
  }

  // Text
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent ?? '').trim();
  }

  return null;
}

function parseXml(text: string): unknown {
  if (typeof DOMParser === 'undefined') {
    throw ToolError.execution('XML parsing is not available in this environment');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'application/xml');
  const parseError = doc.getElementsByTagName('parsererror')[0];
  if (parseError) {
    throw ToolError.validation(parseError.textContent?.trim() || 'Invalid XML');
  }

  const root = doc.documentElement;
  if (!root) return {};
  return { [root.tagName]: xmlToObject(root) };
}

function parseStructured(input: string, format: InputFormat): { parsed: unknown; formatUsed: Exclude<InputFormat, 'auto'> } {
  const text = input.trim();

  const tryJson = () => ({ parsed: parseJsonLenient(text) as unknown, formatUsed: 'json' as const });
  const tryYaml = () => ({ parsed: parseYaml(text) as unknown, formatUsed: 'yaml' as const });
  const tryXml = () => ({ parsed: parseXml(text), formatUsed: 'xml' as const });

  if (format === 'json') return tryJson();
  if (format === 'yaml') return tryYaml();
  if (format === 'xml') return tryXml();

  // auto
  if (looksLikeXml(text)) {
    try {
      return tryXml();
    } catch {
      // fall through
    }
  }

  try {
    return tryJson();
  } catch {
    // fall through
  }

  try {
    return tryYaml();
  } catch {
    // fall through
  }

  // last attempt: XML
  return tryXml();
}

class JsonDiagramVisualizerToolImpl implements IVisualizerTool {
  readonly id = 'json-diagram-visualizer';
  readonly name = 'Tree Diagram Viewer';
  readonly description = 'Visualize JSON, YAML, or XML as a tree diagram on a canvas';
  readonly version = '1.0.0';
  readonly type = 'visualizer' as const;
  readonly category = 'Visualizers';
  readonly editable = true;
  readonly keywords = ['json', 'yaml', 'xml', 'diagram', 'tree', 'graph', 'visualize', 'visualizer'];

  readonly supportedInputTypes: ISemanticType[] = [SEMANTIC_TYPES.JSON, SEMANTIC_TYPES.YAML, SEMANTIC_TYPES.XML];

  readonly outputType = SEMANTIC_TYPES.JSON;

  supportsInput(): boolean {
    return true;
  }

  readonly attributes = [
    {
      name: 'inputFormat',
      type: 'select' as const,
      label: 'Input Format',
      defaultValue: 'auto',
      options: ['auto', 'json', 'yaml', 'xml'],
      description: 'How to parse the input before rendering (auto tries XML, then JSON, then YAML)',
    },
    {
      name: 'maxDepth',
      type: 'number' as const,
      label: 'Max Depth',
      defaultValue: 10,
      description: 'Maximum nesting depth to include in the diagram',
      validation: { min: 1, max: 50 },
    },
    {
      name: 'initialExpanded',
      type: 'boolean' as const,
      label: 'Initially Expanded',
      defaultValue: true,
      description: 'Whether nodes are expanded by default',
    },
    {
      name: 'showTypes',
      type: 'boolean' as const,
      label: 'Show Types',
      defaultValue: true,
      description: 'Display data type indicators and colors',
    },
    {
      name: 'zeroIndexed',
      type: 'boolean' as const,
      label: 'Zero-based Indexing',
      defaultValue: true,
      description: 'Use 0-based indexing (0, 1, 2...) instead of 1-based indexing',
    },
    {
      name: 'search',
      type: 'string' as const,
      label: 'Search',
      defaultValue: '',
      description: 'Search term used by the visualizer',
    },
    {
      name: 'searchMode',
      type: 'select' as const,
      label: 'Search Mode',
      defaultValue: DEFAULT_SEARCH_MODE,
      options: ['filter', 'highlight'],
      description: 'filter hides non-matching branches; highlight keeps everything visible and highlights matches',
    },
    {
      name: 'maxNodes',
      type: 'number' as const,
      label: 'Max Diagram Nodes (Advanced)',
      defaultValue: 1000,
      description: 'Maximum number of nodes to render in the diagram. Lower values prevent browser crashes on large files.',
      validation: { min: 10, max: 5000 },
    },
    {
      name: 'pruningLimit',
      type: 'number' as const,
      label: 'Array/Object Pruning (Advanced)',
      defaultValue: 50,
      description: 'Maximum number of children to show per node before truncating. Prevents single wide nodes from breaking layout.',
      validation: { min: 1, max: 200 },
    },
  ];

  render(context: VisualizerRenderContext): React.ReactElement {
    const { input, config, editMode, onEdit } = context;

    const requestedFormat: InputFormat =
      config.inputFormat === 'json' || config.inputFormat === 'yaml' || config.inputFormat === 'xml' || config.inputFormat === 'auto'
        ? (config.inputFormat as InputFormat)
        : 'auto';

    // Empty input: start with empty object
    const effectiveInput = input.trim() || '{}';

    const parseResult = React.useMemo(() => {
      try {
        const result = parseStructured(effectiveInput, requestedFormat);
        return { parsed: result.parsed, error: null };
      } catch (e) {
        return { 
          parsed: null, 
          error: e instanceof Error ? e.message : 'Unable to parse input' 
        };
      }
    }, [effectiveInput, requestedFormat]);

    if (parseResult.error) {
      return (
        <div className="p-4 text-red-500">
          <div className="font-semibold">Parse Error</div>
          <div className="text-sm mt-1">{parseResult.error}</div>
          <div className="text-xs mt-2 text-muted-foreground">
            Tried format: <span className="font-mono">{requestedFormat}</span>
          </div>
        </div>
      );
    }

    // Search/filter/display state is uncontrolled — see architectural-invariants.md → Visualizer Isolation
    const zeroIndexed = typeof config.zeroIndexed === 'boolean' ? config.zeroIndexed : true;

    // Edit toolbar handlers for root-level adds
    const [addingKey, setAddingKey] = React.useState(false);
    const [newKeyDraft, setNewKeyDraft] = React.useState('');
    const newKeyRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
      if (addingKey) newKeyRef.current?.focus();
    }, [addingKey]);

    const commitAddKey = React.useCallback((key: string) => {
      setAddingKey(false);
      setNewKeyDraft('');
      if (!onEdit || !key.trim()) return;
      try {
        const clone = parseJsonLenient(effectiveInput);
        if (typeof clone === 'object' && clone !== null && !Array.isArray(clone)) {
          (clone as Record<string, unknown>)[key.trim()] = '';
          onEdit(JSON.stringify(clone, null, 2));
        }
      } catch { /* ignore */ }
    }, [effectiveInput, onEdit]);

    const handleAddArrayItem = React.useCallback(() => {
      if (!onEdit) return;
      try {
        const clone = parseJsonLenient(effectiveInput);
        if (Array.isArray(clone)) {
          clone.push('');
          onEdit(JSON.stringify(clone, null, 2));
        }
      } catch { /* ignore */ }
    }, [effectiveInput, onEdit]);

    const isRootArray = React.useMemo(() => {
      try { return Array.isArray(parseJsonLenient(effectiveInput)); } catch { return false; }
    }, [effectiveInput]);

    return (
      <div className="h-full flex flex-col">
        {editMode && onEdit && (
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/20 shrink-0">
            <span className="text-[10px] text-muted-foreground">Edit:</span>
            {!isRootArray && (
              addingKey ? (
                <span className="inline-flex items-center gap-1">
                  <input
                    ref={newKeyRef}
                    className="bg-muted/50 border border-primary/50 rounded px-1 py-0 text-xs font-mono outline-none w-24"
                    placeholder="key name"
                    value={newKeyDraft}
                    onChange={(e) => setNewKeyDraft(e.target.value)}
                    onBlur={() => commitAddKey(newKeyDraft)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commitAddKey(newKeyDraft); }
                      if (e.key === 'Escape') { e.preventDefault(); setAddingKey(false); setNewKeyDraft(''); }
                    }}
                  />
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingKey(true)}
                  className="text-xs px-2 py-0.5 rounded border border-dashed border-border text-muted-foreground hover:border-violet-400 hover:text-violet-400 transition-colors"
                >
                  + Add Key
                </button>
              )
            )}
            {isRootArray && (
              <button
                type="button"
                onClick={handleAddArrayItem}
                className="text-xs px-2 py-0.5 rounded border border-dashed border-border text-muted-foreground hover:border-violet-400 hover:text-violet-400 transition-colors"
              >
                + Add Array Item
              </button>
            )}
          </div>
        )}
        <div className="flex-1 min-h-0">
          <React.Suspense fallback={<div className="flex items-center justify-center h-full text-sm text-muted-foreground">Loading diagram…</div>}>
            <LazyJsonDiagramView
              data={parseResult.parsed}
              maxDepth={(config.maxDepth as number) ?? 10}
              initialExpanded={(config.initialExpanded as boolean) ?? true}
              zeroIndexed={zeroIndexed}
              maxNodes={(config.maxNodes as number) ?? 1000}
              pruningLimit={(config.pruningLimit as number) ?? 50}
              editMode={editMode}
              onEdit={onEdit}
            />
          </React.Suspense>
        </div>
      </div>
    );
  }
}

export const JsonDiagramVisualizerTool = new JsonDiagramVisualizerToolImpl();
